import { useEffect, useRef, useState } from 'react';
import { FlightReport, FlightReportResponse, LogChartsResponse } from '../types';
import { ApiLike } from './seriesClient';

/* The two per-log reads the page shares: /log-charts (overview, flight info) and /report. */

export interface LogData {
  charts: LogChartsResponse | null;
  chartsLoading: boolean;
  chartsError: string;
  report: FlightReport | null;
  reportLoading: boolean;
}

const isSingleLog = (id: string) => !!id && !id.includes(',');

export default function useLogData(api: Pick<ApiLike, 'get'>, logId: string): LogData {
  const [charts, setCharts] = useState<LogChartsResponse | null>(null);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState('');
  const [report, setReport] = useState<FlightReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  // Either read can outlast a log switch; only the current log's answer may land.
  const chartsFor = useRef('');
  const reportFor = useRef('');

  useEffect(() => {
    setCharts(null);
    setChartsError('');
    if (!isSingleLog(logId)) {
      chartsFor.current = '';
      setChartsLoading(false);
      return;
    }
    const id = logId;
    chartsFor.current = id;
    setChartsLoading(true);
    api.get(`/analysis/log-charts/${id}`)
      .then((data: LogChartsResponse) => {
        if (chartsFor.current === id) setCharts(data);
      })
      .catch((err: any) => {
        if (chartsFor.current === id) {
          setChartsError(err?.message || err?.error || 'The server did not return chart data.');
        }
      })
      .finally(() => {
        if (chartsFor.current === id) setChartsLoading(false);
      });
    return () => { chartsFor.current = ''; };
  }, [logId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setReport(null);
    if (!isSingleLog(logId)) {
      reportFor.current = '';
      setReportLoading(false);
      return;
    }
    const id = logId;
    reportFor.current = id;
    setReportLoading(true);
    api.get(`/analysis/report/${id}`, { silent: true })
      .then((data: FlightReportResponse) => {
        if (reportFor.current === id && data?.report) setReport(data.report);
      })
      .catch(() => {})
      .finally(() => {
        if (reportFor.current === id) setReportLoading(false);
      });
    return () => { reportFor.current = ''; };
  }, [logId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { charts, chartsLoading, chartsError, report, reportLoading };
}
